/* ============================================================================
   ENVOLVENTE OPERACIONAL — BK117 B-2

   Digitalizada de la tabla que aportó el operador (OEI_BK117_b-2.xlsx).

   ══════════════════════════════════════════════════════════════════════════
   QUÉ ES ESTA TABLA, Y QUÉ NO ES

   **Es la carta de ALTITUD DE DENSIDAD con los límites de operación.** Cada
   par es (temperatura exterior → altitud de densidad) para una altitud de
   presión dada.

   Se comprobó contra la atmósfera estándar: la pendiente de la tabla es
   **119,4 ft/°C** y la teórica **118,8 ft/°C**. Coinciden, lo que confirma la
   lectura.

   **NO es la carta de peso máximo OEI (Figura 5-2).** Esa sigue faltando.

   Las dos cosas se relacionan así: con esta carta se obtiene la altitud de
   densidad, y CON ESE NÚMERO se entra a la carta OEI para leer el peso máximo.
   Esta tabla resuelve el primer paso, no el segundo.

   Decirlo importa: si se presentara esto como «la carta OEI ya está cargada»,
   alguien podría creer que la aplicación calcula el peso máximo con un motor
   inoperativo, y no lo calcula.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ SE INTERPOLA CON LOS PUNTOS DE LA CARTA Y NO CON LA FÓRMULA

   La fórmula atmosférica da un resultado parecido pero no idéntico: en los
   puntos de la tabla las diferencias llegan a 650 ft. La carta es el documento
   aprobado; la fórmula es una aproximación. Se usa la carta, y donde la carta
   no llega se dice que no llega — no se rellena con la fórmula.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

export const PROCEDENCIA_ENVOLVENTE = {
  fuente: "RFM BK117 B-2, Fig. 2-3 «Altitude-temperature envelope», "
    + "LBA APPROVED Rev. 6, más las coordenadas digitalizadas por el operador",
  aeronave: "BK117 B-2",
  matricula: "OB-2240P",
  digitalizado: "2026-09-03",
  verificacion: "Pendiente de la tabla 119,4 ft/°C contra 118,8 ft/°C teóricos: coincide.",
  advertencia: "Esta es la carta de ALTITUD DE DENSIDAD y los límites de operación. "
    + "NO es la carta de peso máximo OEI (Figura 5-2), que sigue faltando.",
};

/**
 * El número de serie decide dos límites, y no es un detalle administrativo.
 *
 * La Fig. 2-3 dice, con todas las letras:
 *
 *   «15 000 ft limit applies for helicopters up to S/N 7252, if
 *    S/B-MBB-BK117-60-111 not incorporated.
 *    18 000 ft limit applies for helicopters of S/N 7253 and subsequent, or
 *    with S/B-MBB-BK117-60-111 incorporated»
 *
 * Y sobre el área rayada de la carta: «Operation within the hatched area is
 * permissible only for helicopters with the "High Altitude Kit" installed
 * (All helicopter from S/N 7253, and subsequent, or with S/B-MBB-BK117-60-111
 * incorporated)».
 *
 * **OB-2240P es S/N 7074.** Está por debajo de 7252, así que le corresponde el
 * límite de 15000 ft y NO puede operar en el área rayada —salvo que tenga el
 * boletín de servicio incorporado, que es un dato de la aeronave, no de la
 * carta.
 */
export const SERIE_CORTE = 7252;
export const BOLETIN_ALTURA = "S/B-MBB-BK117-60-111";

/**
 * Los puntos de la carta, por altitud de presión.
 *
 * Cada entrada es `[temperaturaC, altitudDensidadFt]`, tal como se leyeron.
 * No se agregan puntos intermedios: los que están son los que el operador
 * midió sobre la curva.
 */
export const CARTA_DENSIDAD = {
  "-2000": [[36, 0], [50, 1700]],
  0: [[15, 0], [50, 3800]],
  2000: [[-7, 0], [46, 5800]],
  4000: [[-24, 0], [42, 7800]],
  6000: [[-42, 0], [38, 9900]],
  8000: [[-45, 2200], [34, 11800]],
  10000: [[-45, 4800], [-30, 6800], [30, 13800]],
  12000: [[-45, 7400], [-30, 9300], [26, 15800]],
  14000: [[-45, 9950], [22, 17900]],
  15000: [[-45, 11100], [4, 17000], [20, 18800]],
  16000: [[-45, 12400], [18, 19800]],
  18000: [[-45, 15000], [-35, 16200], [-28, 17000], [14, 21800]],
};

/**
 * Límites de operación declarados en la tabla.
 *
 * Cada uno dice de dónde salió y qué tan confiable es esa lectura: un límite
 * que se aplica sin saber si se leyó bien es peor que ninguno.
 */
export const LIMITES = {
  temperaturaMaximaC: {
    valor: 50,
    hasta: "Hasta 3800 ft de altitud de densidad (al nivel del mar).",
    confianza: "alta",
    nota: "La tabla declara 50 °C en −2000, 0 y hasta 3800 ft de densidad.",
  },
  temperaturaMinimaSinKitC: {
    valor: -35,
    confianza: "alta",
    nota: "«Not Cold» en la tabla: límite sin equipo de tiempo frío instalado.",
  },
  temperaturaMinimaConKitC: {
    valor: -45,
    confianza: "alta",
    nota: "«Installed Cold» en la tabla: con equipo de tiempo frío instalado.",
  },
  /* CORREGIDO con la Fig. 2-3: los 17000 ft NO son un máximo general.
     Son el máximo para ESTACIONARIO EN EFECTO SUELO, DESPEGUE Y ATERRIZAJE.

     La digitalización anterior lo llamaba «máximo de operación» a secas, que
     es más restrictivo de lo que dice el manual en crucero y menos claro de lo
     que dice en despegue: son dos límites distintos y confundirlos lleva a
     rechazar vuelos posibles o a aceptar despegues que el manual no permite. */
  hoverDespegueAterrizajeFt: {
    valor: 17000,
    confianza: "alta",
    nota: "Fig. 2-3: «Maximum operating altitude for hover in ground effect, "
      + "takeoff and landing (17000 ft DA)». No es el techo de crucero.",
  },

  /* El máximo de operación depende del NÚMERO DE SERIE y del boletín. */
  operacionHasta3000kg: {
    sinBoletinFt: 15000,
    conBoletinFt: 18000,
    confianza: "alta",
    nota: `Fig. 2-3: 15000 ft hasta S/N ${SERIE_CORTE} sin ${BOLETIN_ALTURA}; `
      + `18000 ft desde S/N ${SERIE_CORTE + 1} o con el boletín incorporado.`,
  },

  areaRayada: {
    confianza: "alta",
    nota: `Fig. 2-3: operar dentro del área rayada solo se permite con el `
      + `«High Altitude Kit» instalado (S/N ${SERIE_CORTE + 1} en adelante, o con `
      + `${BOLETIN_ALTURA} incorporado).`,
  },
  /* Este límite se declara pero NO se aplica solo: los puntos de la tabla son
     ambiguos —hay dos valores distintos para −30 °C— y aplicar un límite de
     peso mal leído restringiría o permitiría operaciones sin fundamento. */
  /* AMBIGÜEDAD RESUELTA con la Fig. 2-3.

     Los cuatro puntos que traía la tabla del operador eran DOS COSAS
     distintas mezcladas: (−45, 7400) y (−30, 9300) son puntos de la línea de
     12000 ft de altitud de presión —ya están en CARTA_DENSIDAD— y solo
     (−30, 6800) y (30, 13800) pertenecen a la línea de límite para más de
     3000 kg, que en la figura es la traza gruesa inferior.

     Por eso aparecían dos valores para −30 °C: venían de curvas distintas. */
  sobre3000kg: {
    puntos: [[-30, 6800], [30, 13800]],
    confianza: "media",
    nota: "Fig. 2-3: «Maximum operating altitude, when helicopter gross mass is "
      + "greater than 3000 kg» — la traza gruesa inferior. Los dos puntos se "
      + "leyeron del gráfico; el tramo entre ellos se interpola, y por encima de "
      + "30 °C la línea cae y NO se extrapola.",
  },
};

const num = (v) => (Number.isFinite(v) ? v : null);

/** Las altitudes de presión que tiene la carta, ordenadas. */
export const ALTITUDES_CARTA = Object.keys(CARTA_DENSIDAD)
  .map(Number)
  .sort((a, b) => a - b);

/** Interpola linealmente dentro de una serie de pares ordenados por x. */
function interpolar(pares, x) {
  const orden = [...pares].sort((a, b) => a[0] - b[0]);
  if (orden.length === 0) return null;

  /* Fuera del rango medido NO se extrapola. Una curva de performance
     extrapolada es una invención con apariencia de dato.

     El bucle de abajo ya lo garantiza —solo devuelve valor si `x` cae ENTRE
     dos puntos— pero el corte explícito queda porque hace visible la
     intención: quien lea esto tiene que ver que la decisión existe, no
     deducirla de que el bucle no encuentra tramo.

     Con UN SOLO punto medido el bucle no se ejecuta y devolvería null; el
     corte lo dice antes y con el motivo correcto. */
  if (orden.length === 1) return x === orden[0][0] ? orden[0][1] : null;
  if (x < orden[0][0] || x > orden[orden.length - 1][0]) return null;

  for (let i = 0; i < orden.length - 1; i += 1) {
    const [x1, y1] = orden[i];
    const [x2, y2] = orden[i + 1];
    if (x >= x1 && x <= x2) {
      if (x2 === x1) return y1;
      return y1 + ((y2 - y1) * (x - x1)) / (x2 - x1);
    }
  }
  return null;
}

/**
 * Altitud de densidad leída de la carta.
 *
 * Interpola en temperatura dentro de cada altitud de presión, y después entre
 * las dos altitudes de presión que rodean a la pedida.
 *
 * Devuelve `null` cuando el punto queda fuera de lo medido — no se extrapola.
 */
export function altitudDensidadDeCarta(altitudPresionFt, oatC) {
  const pa = num(altitudPresionFt);
  const oat = num(oatC);
  if (pa === null || oat === null) return null;

  if (pa < ALTITUDES_CARTA[0] || pa > ALTITUDES_CARTA[ALTITUDES_CARTA.length - 1]) return null;

  /* Coincidencia exacta con una altitud de la carta. */
  if (CARTA_DENSIDAD[String(pa)]) {
    return interpolar(CARTA_DENSIDAD[String(pa)], oat);
  }

  const abajo = [...ALTITUDES_CARTA].reverse().find((a) => a < pa);
  const arriba = ALTITUDES_CARTA.find((a) => a > pa);
  if (abajo === undefined || arriba === undefined) return null;

  const yAbajo = interpolar(CARTA_DENSIDAD[String(abajo)], oat);
  const yArriba = interpolar(CARTA_DENSIDAD[String(arriba)], oat);

  /* Si una de las dos altitudes no cubre esa temperatura, no se inventa la
     otra mitad: el punto queda fuera de la carta. */
  if (yAbajo === null || yArriba === null) return null;

  return Math.round(yAbajo + ((yArriba - yAbajo) * (pa - abajo)) / (arriba - abajo));
}

/**
 * Comprueba las condiciones contra los límites declarados.
 *
 * Devuelve la lista de hallazgos. Un límite con confianza baja NO produce un
 * veredicto: se menciona para que alguien lo mire.
 */
/**
 * El máximo de operación que le corresponde a ESTA aeronave.
 *
 * Depende del número de serie y del boletín, no del vuelo. Con S/N 7074 y sin
 * boletín son 15000 ft; presentarle 18000 a esa aeronave sería habilitar 3000
 * ft que el manual no le da.
 */
export function maximoOperacion({ serie = null, boletinIncorporado = false } = {}) {
  /* `Number(null)` y `Number("")` dan 0, que es finito: sin este filtro, una
     serie ausente se tomaba por declarada y el aviso de «se aplicó el límite
     por defecto» nunca aparecía. El límite salía bien —15000 ft— pero nadie
     se enteraba de que era una suposición. */
  const crudo = typeof serie === "string" ? serie.trim() : serie;
  const n = (crudo === null || crudo === undefined || crudo === "")
    ? NaN
    : Number(crudo);
  const alcanza = boletinIncorporado === true
    || (Number.isFinite(n) && n > SERIE_CORTE);

  return {
    ft: alcanza ? LIMITES.operacionHasta3000kg.conBoletinFt : LIMITES.operacionHasta3000kg.sinBoletinFt,
    areaRayadaPermitida: alcanza,
    /* Sin número de serie se aplica el límite MÁS BAJO. En la duda, el lado
       seguro: dar 18000 ft a una aeronave que no los tiene es lo que no se
       puede hacer. */
    /* «Por defecto» significa: no se declaró la serie Y no se declaró el
       boletín, así que el límite salió de una suposición, no de un dato. La
       comparación anterior usaba `!== true`, que con el valor por omisión
       `false` daba siempre verdadero y nunca marcaba el caso. */
    porDefecto: !Number.isFinite(n) && !boletinIncorporado,
    motivo: alcanza
      ? `S/N ${serie ?? "—"}${boletinIncorporado ? ` con ${BOLETIN_ALTURA}` : ""}: `
        + `${LIMITES.operacionHasta3000kg.conBoletinFt} ft.`
      : `S/N ${serie ?? "sin declarar"} sin ${BOLETIN_ALTURA}: `
        + `${LIMITES.operacionHasta3000kg.sinBoletinFt} ft, y el área rayada de la `
        + "carta no está permitida.",
  };
}

export function comprobarEnvolvente(entrada) {
  /* Se desestructura DESPUÉS de comprobar: `{...} = null` revienta, y esto lo
     llama la pantalla con lo que haya. */
  const {
    altitudPresionFt = null,
    oatC = null,
    altitudDensidadFt = null,
    kitFrioInstalado = false,
    pesoBrutoKg = null,
    /* Fase del vuelo: el límite de 17000 ft aplica a estacionario en efecto
       suelo, despegue y aterrizaje. En crucero manda el otro. */
    fase = "crucero",
    serie = null,
    boletinIncorporado = false,
  } = (entrada && typeof entrada === "object") ? entrada : {};

  const hallazgos = [];
  const oat = num(oatC);

  /* La altitud de densidad: la de la carta si se puede, y si no la que venga
     dada. Se dice cuál se usó — no es lo mismo un número de la carta aprobada
     que uno calculado con la fórmula general. */
  const deCarta = altitudDensidadDeCarta(altitudPresionFt, oat);
  const da = deCarta !== null ? deCarta : num(altitudDensidadFt);
  const origenDA = deCarta !== null ? "carta" : (num(altitudDensidadFt) !== null ? "calculada" : null);

  if (deCarta === null && num(altitudPresionFt) !== null && oat !== null) {
    hallazgos.push({
      nivel: "precaucion",
      texto: "Esta combinación de altitud y temperatura queda fuera de los puntos "
        + "medidos de la carta. No se extrapola: verifica contra el manual.",
    });
  }

  if (oat !== null) {
    const minimo = kitFrioInstalado
      ? LIMITES.temperaturaMinimaConKitC.valor
      : LIMITES.temperaturaMinimaSinKitC.valor;

    if (oat < minimo) {
      hallazgos.push({
        nivel: "alto",
        texto: `${oat} °C está por debajo del mínimo de ${minimo} °C`
          + (kitFrioInstalado ? " con equipo de tiempo frío." : " sin equipo de tiempo frío.")
          /* Si el frío está entre los dos límites, el equipo de tiempo frío
             resuelve el problema: decirlo evita cancelar un vuelo que se puede
             hacer. La comparación anterior usaba `>=` con el mínimo CON kit y
             nunca se cumplía para −40. */
          + (!kitFrioInstalado && oat > LIMITES.temperaturaMinimaConKitC.valor
            ? ` Con equipo de tiempo frío el mínimo baja a ${LIMITES.temperaturaMinimaConKitC.valor} °C.`
            : ""),
      });
    }

    if (oat > LIMITES.temperaturaMaximaC.valor) {
      hallazgos.push({
        nivel: "alto",
        texto: `${oat} °C supera el máximo de ${LIMITES.temperaturaMaximaC.valor} °C.`,
      });
    }
  }

  const maximo = maximoOperacion({ serie, boletinIncorporado });

  /* El límite de 17000 ft es de ESTACIONARIO EN EFECTO SUELO, DESPEGUE Y
     ATERRIZAJE. En crucero no aplica: aplicarlo siempre rechazaría vuelos que
     el manual permite. */
  const enFaseDeSuelo = fase === "despegue" || fase === "aterrizaje" || fase === "hover";
  if (enFaseDeSuelo && da !== null && da > LIMITES.hoverDespegueAterrizajeFt.valor) {
    hallazgos.push({
      nivel: "alto",
      texto: `${Math.round(da)} ft de altitud de densidad supera el máximo de `
        + `${LIMITES.hoverDespegueAterrizajeFt.valor} ft para estacionario en efecto `
        + "suelo, despegue y aterrizaje.",
    });
  }

  /* Y el máximo de operación según la serie. */
  if (da !== null && da > maximo.ft) {
    hallazgos.push({
      nivel: "alto",
      texto: `${Math.round(da)} ft supera el máximo de operación de esta aeronave. ${maximo.motivo}`,
    });
  }

  /* Sin número de serie se aplicó el límite bajo: hay que decirlo, o alguien
     puede creer que la aeronave está más limitada de lo que está. */
  if (maximo.porDefecto) {
    hallazgos.push({
      nivel: "precaucion",
      texto: `Sin número de serie declarado se aplica el límite más bajo `
        + `(${maximo.ft} ft). Con ${BOLETIN_ALTURA} incorporado o desde S/N `
        + `${SERIE_CORTE + 1} el máximo sube a ${LIMITES.operacionHasta3000kg.conBoletinFt} ft.`,
    });
  }

  /* El límite de más de 3000 kg se MENCIONA pero no se aplica: sus puntos son
     ambiguos, y aplicar un límite mal leído restringiría o permitiría
     operaciones sin fundamento. */
  if (num(pesoBrutoKg) !== null && pesoBrutoKg > 3000) {
    hallazgos.push({
      nivel: "precaucion",
      texto: `Con ${Math.round(pesoBrutoKg)} kg aplica el límite de altitud para más `
        + "de 3000 kg. La tabla lo declara pero sus puntos son ambiguos: verificalo "
        + "contra el RFM antes de operar cerca del techo.",
    });
  }

  return {
    altitudDensidadFt: da,
    origenDA,
    maximoOperacionFt: maximo.ft,
    areaRayadaPermitida: maximo.areaRayadaPermitida,
    dentro: !hallazgos.some((h) => h.nivel === "alto"),
    hallazgos,
    procedencia: PROCEDENCIA_ENVOLVENTE,
  };
}
